import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  getErrorMessage,
  validate,
  ValType,
} from "../../utils/ValidationFunctions";
import { postRegisterRequest } from "../../services/api/user/postRegisterRequest";
import { postVerificationRequest } from "../../services/api/user/postVerificationRequest";

// Redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  AuthStatus,
  getAuthStatus,
  setAuthState,
  setAuthToken,
} from "../../redux/reducers/auth";

// Individual form components
import RegistEmailForm from "./RegistEmailForm";
import RegistPasswordForm from "./RegistPasswordForm";
import RegistDisplayNameForm from "./RegistDisplayNameForm";
import RegistTerms from "./RegistTerms";
import RegistVerificationForm from "./RegistVerificationForm";
import RegistComplete from "./RegistComplete";

import styles from "./p-register.module.scss";
import { useMetaData } from "../../utils/useMetaData";

export const enum RegistStep {
  Email,
  Password,
  DisplayName,
  Terms,
  Verification,
  Complete,
}

const enum RegistVerifiyState {
  Loading,
  Failed,
  Ready,
}

type RegistFormState = {
  email: [string, boolean];
  pass: [string, boolean];
  confirm_pass: [string, boolean];
  display_name: [string, boolean];
  pass_error: string;
  step: RegistStep;
  didMount: boolean;
};

const RegisterPage: React.FC = () => {
  const expiry_time = 1800000;
  const auth_status = useAppSelector(getAuthStatus);
  const dispatch = useAppDispatch();

  const [registState, setRegistState] = useState<RegistFormState>({
    email: ["", false],
    pass: ["", false],
    confirm_pass: ["", false],
    display_name: ["", false],
    pass_error: "",
    step: RegistStep.Email,
    didMount: false,
  });

  const [verifyState, setVerifyState] = useState(RegistVerifiyState.Loading);
  const [verifyCode, setVerifyCode] = useState("");

  useEffect(() => {
    const state = registState;
    if (!state.didMount) {
      state.didMount = true;
      const localStateCookie = localStorage.getItem("sonic_reg_state");

      if (localStateCookie !== null) {
        const now = new Date();

        const localState = JSON.parse(localStateCookie);
        if (localState.expire > now.getTime()) {
          state.email = localState.value.email;
          state.pass = localState.value.pass;
          state.confirm_pass = localState.value.confirm_pass;
          state.step = localState.value.step;

          if (localState.value.step == RegistStep.Verification) {
            setVerifyState(RegistVerifiyState.Ready);
          }

          // TODO: This
          setRegistState({ ...state });
        }
      }
    }
  });

  function handleEmailChange(value: string) {
    const state = registState;

    state.email = ["", false];
    setRegistState({ ...state });

    if (value !== undefined) {
      state.email = [value, true];
    } else {
      state.email = [value, false];
    }

    setRegistState({ ...state });
  }

  function handleEmailSubmit() {
    const now = new Date();
    const state = registState;

    if (state.email[1] !== false) {
      state.step = RegistStep.Password;

      localStorage.setItem(
        "sonic_reg_state",
        JSON.stringify({
          value: state,
          expire: now.getTime() + expiry_time,
        })
      );

      setRegistState({ ...state });
    }
  }

  function handlePassChange(value: string, name: string) {
    const state = registState;

    if (value !== undefined) {
      if (name == "sonic_pass") {
        state.pass = [value, true];
      } else if (name == "sonic_pass_confirm") {
        state.confirm_pass = [value, true];
      }
    } else {
      if (name == "sonic_pass") {
        state.pass = [value, false];
      } else if (name == "sonic_pass_confirm") {
        state.confirm_pass = [value, false];
      }
    }

    setRegistState({ ...state });
  }

  async function handlePassSubmit() {
    const now = new Date();
    const state = registState;

    if (state.pass[1] == false || state.confirm_pass[1] == false) {
      return;
    }

    const validation = await validate(
      state.pass[0],
      ValType.PasswordMatches,
      state.confirm_pass[0]
    );

    if (!validation) {
      state.pass_error = getErrorMessage(ValType.PasswordMatches);
      setRegistState({ ...state });
      return;
    }

    state.step = RegistStep.DisplayName;

    localStorage.setItem(
      "sonic_reg_state",
      JSON.stringify({
        value: state,
        expire: now.getTime() + expiry_time,
      })
    );

    setRegistState({ ...state });
  }

  function handleDisplayNameChange(value: string) {
    const state = registState;

    if (value !== "") {
      state.display_name = [value, true];
    } else {
      state.display_name = [value, false];
    }

    setRegistState({ ...state });
  }

  function handleDisplayNameSubmit() {
    const state = registState;

    if (
      state.display_name[1] !== false &&
      state.pass[1] !== false &&
      state.confirm_pass[1] !== false &&
      state.email[1] !== false
    ) {
      state.step = RegistStep.Terms;
      setRegistState({ ...state });
    }
  }

  async function handleTermsSubmit() {
    const now = new Date();
    const state = registState;

    if (
      state.display_name[1] !== false &&
      state.pass[1] !== false &&
      state.confirm_pass[1] !== false &&
      state.email[1] !== false
    ) {
      state.step = RegistStep.Verification;
      setRegistState({ ...state });

      try {
        const resp = await postRegisterRequest(
          state.display_name[0],
          state.pass[0],
          state.confirm_pass[0],
          state.email[0]
        );

        if (resp) {
          localStorage.setItem(
            "sonic_reg_state",
            JSON.stringify({
              value: state,
              expire: now.getTime() + expiry_time,
            })
          );

          setVerifyState(RegistVerifiyState.Ready);
        } else {
          localStorage.removeItem("sonic_reg_state");
          setVerifyState(RegistVerifiyState.Failed);
        }
      } catch (error) {
        localStorage.removeItem("sonic_reg_state");
        setVerifyState(RegistVerifiyState.Failed);
      }
    }
  }

  function handleVerifyChange(value: string) {
    setVerifyCode(value);
  }

  async function handleVerifySubmit() {
    if (registState.email[1] !== false && verifyCode !== "") {
      const resp = await postVerificationRequest(
        verifyCode,
        registState.email[0]
      );

      if (resp !== null && typeof resp == "string") {
        localStorage.removeItem("sonic_reg_state");
        setRegistState({ ...registState, step: RegistStep.Complete });
        setTimeout(() => {
          dispatch(setAuthState(AuthStatus.Authenticated));
          dispatch(setAuthToken(resp));
        }, 4000);
      } else {
        setVerifyState(RegistVerifiyState.Failed);
      }
    }
  }

  const handleBackward = () => {
    setRegistState({ ...registState, step: registState.step - 1 });
  };

  useMetaData({
    title: "会員登録",
    description:
      "SELeCTの会員登録はこちら。サイトへの情報掲載、プロフィールへのオファー、プロジェクトへの応募がご利用いただけるようになります。",
    imgUrl: "",
  });

  function genPage(step: RegistStep) {
    switch (step) {
      default:
      case RegistStep.Email:
        return (
          <RegistEmailForm
            email={registState.email[0]}
            submit_handler={handleEmailSubmit}
            input_handler={handleEmailChange}
          />
        );
      case RegistStep.Password:
        return (
          <RegistPasswordForm
            pass={registState.pass[0]}
            confirm_pass={registState.confirm_pass[0]}
            submit_handler={handlePassSubmit}
            input_handler={handlePassChange}
            backward_handler={handleBackward}
            error={registState.pass_error}
          />
        );
      case RegistStep.DisplayName:
        return (
          <RegistDisplayNameForm
            display_name={registState.display_name[0]}
            submit_handler={handleDisplayNameSubmit}
            input_handler={handleDisplayNameChange}
            backward_handler={handleBackward}
          />
        );
      case RegistStep.Terms:
        return (
          <RegistTerms
            submit_handler={handleTermsSubmit}
            backward_handler={handleBackward}
            // input_handler={handleEmailChange}
          />
        );
      case RegistStep.Verification:
        return (
          <RegistVerificationForm
            submit_handler={handleVerifySubmit}
            input_handler={handleVerifyChange}
            verification_state={verifyState}
          />
        );

      case RegistStep.Complete:
        // return <h1>TODO: Complete page</h1>;
        return <RegistComplete />;
    }
  }
  return (
    <div
      className={
        registState.step == RegistStep.Complete
          ? `${styles["p-register__container"]} ${styles["is-complete"]}`
          : `${styles["p-register__container"]}`
      }
    >
      {auth_status !== AuthStatus.Authenticated ? (
        genPage(registState.step)
      ) : (
        <Navigate
          to={{ pathname: "/mypage", search: "?showTutorial=true" }}
          replace
        />
      )}
    </div>
  );
};

export default RegisterPage;
